import React from "react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import type { UnitFeature } from "@/types";
import { TableLayout, WidgetLayout } from "@/types/enums";
import { useUnitItemContext } from "@/hooks/useUnitItemContext";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useFacility } from "@/hooks/useFacility";
import { useIsAnnexFacility } from "@/hooks/useIsAnnexFacility";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface UnitFeaturesProps {
    features: UnitFeature[];
    tier?: string;
}

/**
 * Renders the amenity list for a unit card.
 *
 * Layout 1 (default): renders an inline "View More"/"View Less" toggle when
 * the visible amenity count exceeds 2 and the host theme is configured to
 * truncate at the current viewport (grid cards always; row cards on
 * `max-width: 767px`). When expanded, the wrapping `<div class="unit-item__features">`
 * receives a `features-expanded` class — host themes are expected to use this
 * class to override their own `:nth-child(n+3) { display: none }` truncation
 * (the storage-theme-payload theme does this via `.features-expanded li { block }`).
 *
 * Layout 2: renders a "More Amenities" tooltip/modal trigger instead of the
 * inline toggle. The host theme's truncation CSS still applies.
 */
export const UnitFeatures = ({ features, tier }: UnitFeaturesProps) => {
    const bgClass =
        tier === "tierB"
            ? "tw:before:bg-[var(--secondary)]"
            : "tw:before:bg-[var(--primary)]";

    const [openId, setOpenId] = React.useState<number | null>(null);
    const [facilityTooltipOpen, setFacilityTooltipOpen] = React.useState(false);
    const [moreAmenitiesOpen, setMoreAmenitiesOpen] = React.useState(false);
    const [expanded, setExpanded] = React.useState(false);

    const { widgetLayout, tableLayout } = useDisplayOptionContext();
    const isLayout2 = widgetLayout === WidgetLayout.LAYOUT_2;
    const isGrid = tableLayout === TableLayout.GRID;
    const isMobile = useMediaQuery("(max-width: 767px)");
    const { unitGroup } = useUnitItemContext();
    const { facility } = useFacility(unitGroup.representative_unit.facility_id);
    const { isAnnex } = useIsAnnexFacility(unitGroup.representative_unit.facility_id);

    const showAnnex = Boolean(facility && isAnnex);
    const annexCount = showAnnex ? 1 : 0;
    const totalItems = annexCount + features.length;
    // Layout 1 truncates to 2 visible items with an inline View More/Less
    // toggle. Grid cards truncate at every viewport; row cards only truncate
    // on mobile (<768px) — matches host theme's `:nth-child(n+3)` rules.
    // Layout 2 keeps its own "More Amenities" modal trigger (rendered below).
    const truncatesAtThisViewport = isGrid || isMobile;
    const showLayout1Toggle = !isLayout2 && totalItems > 2 && truncatesAtThisViewport;
    const visibleFeatures =
        showLayout1Toggle && !expanded
            ? features.slice(0, Math.max(0, 2 - annexCount))
            : features;

    // The `features-expanded` class is consumed by the host theme to override
    // its `:nth-child(n+3) { display: none }` truncation rule when expanded.
    const wrapperClassName = `unit-item__features${
        showLayout1Toggle && expanded ? " features-expanded" : ""
    }`;

    return (
        <div className={wrapperClassName}>
            <ul className="unit-item-features">
                {showAnnex && facility && (
                    <li
                        className={`unit-item-features__item`}
                        id={`feature-annex`}
                    >
                        <TooltipProvider>
                            <Tooltip
                                open={facilityTooltipOpen}
                                onOpenChange={setFacilityTooltipOpen}
                            >
                                <TooltipTrigger asChild>
                                    <span
                                        onClick={() => setFacilityTooltipOpen(!facilityTooltipOpen)}
                                        className="tw:font-bold"
                                    >
                                        <span className="feature-icon"></span>
                                        {facility.name}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent className="tw:max-w-xs bg-white tw:shadow-md">
                                    <div>
                                        <p className="tw:text-sm tw:text-black">
                                            This unit is located at our <strong>{facility.name} location</strong>. Annex locations have separate controlled-access entrances and are managed by our staff working from an office at the main location.
                                        </p>
                                        </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </li>
                )}
                {visibleFeatures.length > 0 ? visibleFeatures.map((feature) => (
                    <li
                        key={feature.id}
                        className={`${bgClass} unit-item-features__item`}
                        id={`feature-${feature.id}`}
                    >
                        {feature.description ? (
                            <TooltipProvider>
                                <Tooltip
                                    open={openId === feature.id}
                                    onOpenChange={(isOpen) =>
                                        setOpenId(isOpen ? feature.id : null)
                                    }
                                >
                                    <TooltipTrigger asChild>
                                        <span
                                            onClick={() =>
                                                setOpenId(
                                                    openId === feature.id
                                                        ? null
                                                        : feature.id,
                                                )
                                            }
                                        >
                                            <span className="feature-icon"></span>
                                            {feature.name}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{feature.description}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        ) : (
                            <span>{feature.name}</span>
                        )}
                    </li>
                )) : <></>}
            </ul>
            {isLayout2 && features.length > 2 && (
                <TooltipProvider>
                    <Tooltip
                        open={moreAmenitiesOpen}
                        onOpenChange={setMoreAmenitiesOpen}
                    >
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className="features-toggle-btn"
                                onClick={() =>
                                    setMoreAmenitiesOpen(!moreAmenitiesOpen)
                                }
                                aria-label="Show all unit features"
                            >
                                More Amenities
                            </button>
                        </TooltipTrigger>
                        <TooltipContent className="se-unit-table more-amenities-modal">
                            <ul className="feature-list">
                                {features.map((feature) => (
                                    <li key={feature.id}>{feature.name}</li>
                                ))}
                            </ul>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
            {showLayout1Toggle && (
                <button
                    type="button"
                    className="features-toggle-btn"
                    onClick={() => setExpanded((v) => !v)}
                    aria-expanded={expanded}
                    aria-label={
                        expanded
                            ? "Show fewer unit features"
                            : "Show all unit features"
                    }
                >
                    {expanded ? "View Less" : "View More"}
                </button>
            )}
        </div>
    );
};
